/**
 * Donor Events API client (weavein) — Fundraising Suite, donor management.
 * Wraps /api/donor-events/* (calendar_events + event_attendees, Training engine)
 * via authFetch (Authorization + X-CBO-ID).
 */

import { authFetchJson } from '@/lib/authFetch';
import { getWeaveinApiUrl } from '@/src/config';

export interface EventMaterial {
  title: string;
  url: string;
  kind: string; // slidesEmbed provider: canva | google_slides | office | pdf | video | link
}

export interface DonorEventAttendee {
  id: number;
  member_id?: number | null;
  external_email?: string | null;
  email?: string | null;
  name: string;
  response_status: string;
  attended?: boolean | null;
  check_in_time?: string | null;
  /** When the invitation was actually SENT — null means invited-but-never-notified. */
  invite_sent_at?: string | null;
  invite_channels?: string | null;
  invite_send_count?: number;
  invite_last_error?: string | null;
  given?: number;
}

export interface DonorEvent {
  id: number;
  cbo_id: number; // the /donate QR link needs this — it used to render as `/donate/`
  title: string;
  description?: string | null;
  start_time: string | null;
  end_time: string | null;
  location_type: string;
  location_address?: string | null;
  meeting_provider?: string | null;
  meeting_link?: string | null;
  status: string;
  cohort_id?: number | null;
  cohort_name?: string | null;
  invited: number;
  /** People actually reached. `invited` is roster rows — they are not the same. */
  notified: number;
  accepted: number;
  declined: number;
  attended: number;
  gift_count: number;
  public_url?: string;
  raised: number; // sum of succeeded donations tagged to this event
  materials: EventMaterial[]; // presentation deck(s) attached to the event
  attendees?: DonorEventAttendee[];
}

export interface PublicDonorEvent {
  id: number;
  cbo_id: number;
  org_name?: string | null;
  title: string;
  description?: string | null;
  start_time: string | null;
  end_time: string | null;
  location_type: string;
  location_address?: string | null;
  meeting_provider?: string | null;
}

export interface DonorEventCreate {
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  location_type?: string;
  location_address?: string;
  meeting_provider?: string;
  meeting_link?: string;
  cohort_id?: number;
  /** Fan the cohort out to invitations on create, instead of just storing the id. */
  invite_cohort?: boolean;
  invite_channels?: InviteChannel[];
}

export type DonorEventUpdate = Partial<DonorEventCreate> & { status?: string };

export type InviteChannel = 'email' | 'sms' | 'portal';

export interface DeliveryResult {
  invited?: number;
  notified: number;
  email?: number;
  sms?: number;
  portal?: number;
  skipped?: number;
  recipients?: number;
  failures?: { name?: string | null; reason: string }[];
}

export async function listDonorEvents(upcomingOnly = false): Promise<DonorEvent[]> {
  return authFetchJson<DonorEvent[]>(`/api/donor-events/events${upcomingOnly ? '?upcoming_only=true' : ''}`);
}

export async function createDonorEvent(payload: DonorEventCreate): Promise<DonorEvent> {
  return authFetchJson<DonorEvent>('/api/donor-events/events', { method: 'POST', jsonBody: payload });
}

export async function getDonorEvent(id: number): Promise<DonorEvent> {
  return authFetchJson<DonorEvent>(`/api/donor-events/events/${id}`);
}

export async function updateDonorEvent(id: number, payload: DonorEventUpdate): Promise<DonorEvent> {
  return authFetchJson<DonorEvent>(`/api/donor-events/events/${id}`, { method: 'PUT', jsonBody: payload });
}

/** Cancel (default) or hard-delete. Hard delete is refused once gifts are tagged. */
export async function deleteDonorEvent(id: number, hard = false): Promise<void> {
  await authFetchJson<void>(`/api/donor-events/events/${id}${hard ? '?hard=true' : ''}`, { method: 'DELETE' });
}

export async function inviteToDonorEvent(
  id: number,
  payload: {
    member_id?: number;
    external_email?: string;
    external_name?: string;
    external_phone?: string;
    channels?: InviteChannel[];
    subject?: string;
    body?: string;
    notify?: boolean;
  },
): Promise<DeliveryResult & { event_id: number }> {
  return authFetchJson(`/api/donor-events/events/${id}/invite`, { method: 'POST', jsonBody: payload });
}

/** Invite a whole cohort from the event you are looking at. */
export async function inviteCohortToThisEvent(
  eventId: number,
  cohortId: number,
  payload: { channels?: InviteChannel[]; subject?: string; body?: string } = {},
): Promise<DeliveryResult & { cohort_id: number }> {
  return authFetchJson(`/api/donor-events/events/${eventId}/invite-cohort/${cohortId}`, {
    method: 'POST',
    jsonBody: payload,
  });
}

/** Re-send invitations, or send a reminder / thank-you, to a slice of the roster. */
export async function broadcastDonorEvent(
  id: number,
  payload: {
    kind?: 'invite' | 'reminder' | 'thanks';
    response_statuses?: string[];
    only_unnotified?: boolean;
    attended_only?: boolean;
    channels?: InviteChannel[];
    subject?: string;
    body?: string;
  },
): Promise<DeliveryResult> {
  return authFetchJson(`/api/donor-events/events/${id}/broadcast`, { method: 'POST', jsonBody: payload });
}

/** Turn event guests into fundraising pipeline cards bound to their donor record. */
export async function eventGuestsToPipeline(
  id: number,
  payload: { member_ids?: number[]; attended_only?: boolean; stage?: string; amount?: number } = {},
): Promise<{ created: number; skipped_existing: number; source?: string }> {
  return authFetchJson(`/api/donor-events/events/${id}/to-pipeline`, { method: 'POST', jsonBody: payload });
}

export interface GivingStation {
  event_id: number;
  event_title: string;
  donate_url: string;
  public_url: string;
  qr_image: string | null; // data:image/png;base64 — rendered by the backend QR service
  raised: number;
  gift_count: number;
  attended: number;
  by_method: { method: string; count: number; total: number }[];
  recent_gifts: { id: number; amount: number; method: string; donor_name: string; created_at: string | null }[];
}

export async function getGivingStation(id: number): Promise<GivingStation> {
  return authFetchJson<GivingStation>(`/api/donor-events/events/${id}/giving-station`);
}

export async function rsvpDonorEvent(
  id: number,
  payload: { member_id?: number; external_email?: string; response_status: string },
): Promise<unknown> {
  return authFetchJson(`/api/donor-events/events/${id}/rsvp`, { method: 'POST', jsonBody: payload });
}

export async function checkInDonorEvent(
  id: number,
  payload: { member_id?: number; external_email?: string; external_name?: string; attended?: boolean },
): Promise<{ event_id: number; attended: boolean; attended_count: number }> {
  return authFetchJson(`/api/donor-events/events/${id}/attendance`, { method: 'POST', jsonBody: payload });
}

// Presentation materials (E) — replace the whole deck list for an event.
export async function setDonorEventMaterials(
  id: number,
  materials: EventMaterial[],
): Promise<{ event_id: number; materials: EventMaterial[] }> {
  return authFetchJson(`/api/donor-events/events/${id}/materials`, {
    method: 'PUT',
    jsonBody: { materials },
  });
}

// Public (unauthenticated) — donor event landing + RSVP (D). Plain fetch, no auth.
export async function getPublicDonorEvent(id: string | number): Promise<PublicDonorEvent> {
  const res = await fetch(getWeaveinApiUrl(`/api/donor-events/public/${encodeURIComponent(String(id))}`));
  if (!res.ok) throw new Error('Event not found');
  return res.json();
}

export interface MemberDonorEvent {
  id: number;
  title: string;
  start_time: string | null;
  end_time: string | null;
  status: string;
  response_status: string;
  attended?: boolean | null;
}

// Donor events a member is invited to / attended (donor-record Engagement view).
export async function listMemberDonorEvents(memberId: number): Promise<MemberDonorEvent[]> {
  return authFetchJson<MemberDonorEvent[]>(`/api/donor-events/member/${memberId}`);
}

export async function publicRsvpDonorEvent(
  id: string | number,
  payload: { name?: string; email: string; response_status?: string },
): Promise<{ event_id: number; response_status: string }> {
  const res = await fetch(getWeaveinApiUrl(`/api/donor-events/public/${encodeURIComponent(String(id))}/rsvp`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error('Could not submit your RSVP');
  return res.json();
}
